import { memo } from "react";
import { cn } from "../lib/classNames";
import { PinCountSelect } from "./PinCountSelect";

interface Props {
  /** マップに表示しようとしている件数 = min(maxPinCount, filteredCount)。 */
  displayCount: number;
  /** フィルタ/検索適用後の総件数 (上限カット前)。 */
  filteredCount: number;
  /** 現時点で resolve 済みの件数。 */
  resolvedCount: number;
  isLoading: boolean;
  /** ピン表示上限の現在値。dropdown の選択値。 */
  maxPinCount: number;
  /** ピン表示上限の変更ハンドラ。 */
  onMaxPinCountChange: (next: number) => void;
  /** dropdown の最小値。 */
  minPinCount: number;
  /** dropdown の最大値。 */
  maxPinCountLimit: number;
  /** ハンバーガーメニューを開くハンドラ。 */
  onMenuOpen: () => void;
}

/**
 * `React.memo` でラップ: Shell の無関係な state 変化 (filterExpanded,
 * searchVisible, menuOpen, showMobileList 等) で Shell が re-render しても、
 * Header の props が変わっていなければ再描画を skip する。
 * props は primitive または useState setter で安定参照なので memo が効く。
 */
export const Header = memo(function Header({
  displayCount,
  filteredCount,
  resolvedCount,
  isLoading,
  maxPinCount,
  onMaxPinCountChange,
  minPinCount,
  maxPinCountLimit,
  onMenuOpen,
}: Props) {
  const isCapped = filteredCount > maxPinCount;

  return (
    <header className="safe-top z-30 flex shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-slate-950/95 px-3 pb-2 backdrop-blur-xl sm:gap-3 sm:px-4 sm:pb-2.5">
      <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
        <img
          src="/icon-192.png"
          alt=""
          width={36}
          height={36}
          className="h-8 w-8 shrink-0 rounded-md sm:h-9 sm:w-9"
          decoding="async"
        />
        <div className="min-w-0">
          <h1 className="truncate text-[14px] font-bold leading-tight tracking-tight text-slate-50 sm:text-[15px]">
            食べログ ラーメン百名店マップ
          </h1>
          <p className="hidden text-[11px] leading-tight text-slate-400 sm:block">
            Hyakumeiten Ramen · Google Maps
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* ピン表示上限は PC のみ Header に表示。SP では FilterBar の展開セクションに
            描画される (App.tsx 側で SP の <FilterBar> に prop を渡す)。
            ブレイクポイントは useBreakpoint("(min-width: 1024px)") に合わせて lg。 */}
        <div className="hidden lg:inline-flex">
          <PinCountSelect
            value={maxPinCount}
            onChange={onMaxPinCountChange}
            min={minPinCount}
            max={maxPinCountLimit}
            showLabel
          />
        </div>
        {isLoading && (
          <span
            className="h-2 w-2 animate-pulse rounded-full bg-amber-400"
            aria-label="loading"
          />
        )}
        {/* SP では件数表示を Header から外し、map overlay の bottom-left pill に移動する
            (App.tsx の !isDesktop 分岐を参照)。縦スペース節約 + 片手操作時に親指で
            届く位置に配置するため。PC (lg 以上) は従来通り Header 右側に出す。 */}
        <span
          className={cn(
            "hidden rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-300 lg:inline-flex",
          )}
          title={
            isCapped
              ? `フィルタ ${filteredCount} 件中、上限 ${maxPinCount} 件まで表示`
              : undefined
          }
        >
          {resolvedCount}/{displayCount} 件
          {isCapped && (
            <span className="ml-1 text-slate-500">/{filteredCount}</span>
          )}
        </span>
        {/* ハンバーガーメニュー: PC/SP とも右上固定。サイト全体のナビゲーション
            (About / Privacy / Terms / Disclaimer) を Drawer で開く。 */}
        <button
          type="button"
          onClick={onMenuOpen}
          aria-label="メニューを開く"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-slate-800/80 text-slate-200 transition hover:bg-slate-700"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>
    </header>
  );
});
